import type { PropsWithChildren } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useSettings } from '@wu/widget-core';
import { useChat } from '../../chat';
import { useWindowPosition } from '../utils';

const DEFAULT_BOTTOM_SPACING = 0;

export const EmbedWindow = ({ children }: PropsWithChildren) => {
  const { openChatWindow } = useChat();
  const { right, top: topSpacing, left, width, options } = useWindowPosition();
  const { renderThumbnailMode } = useSettings();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [verticalSpacing, setVerticalSpacing] = useState({});

  const calculateSpace = useCallback(() => {
    if (!containerRef.current) return;

    const { height } = containerRef.current?.getBoundingClientRect() || {};
    const { top } =
      (containerRef.current?.parentNode as Element)?.getBoundingClientRect() ||
      {};

    const isSpaceAbove = top > height;

    setVerticalSpacing({
      bottom: isSpaceAbove ? DEFAULT_BOTTOM_SPACING : 'auto',
      top: isSpaceAbove ? 'auto' : topSpacing
    });
  }, [topSpacing]);

  const throttledCalculateSpace: (() => void) & { timer?: NodeJS.Timeout } =
    useCallback(() => {
      if (!throttledCalculateSpace?.timer) {
        calculateSpace();
        throttledCalculateSpace.timer = setTimeout(() => {
          delete throttledCalculateSpace.timer;
        }, 100);
      }
    }, [calculateSpace]);

  useEffect(() => {
    calculateSpace();

    window.addEventListener('scroll', throttledCalculateSpace);

    return () => {
      window.removeEventListener('scroll', throttledCalculateSpace);
    };
  }, [calculateSpace, throttledCalculateSpace]);

  useEffect(() => {
    if (!openChatWindow) return;

    calculateSpace();
  }, [calculateSpace, openChatWindow]);

  return (
    <Container
      ref={containerRef}
      _width={width}
      _openWindow={openChatWindow}
      _thumbnailMode={renderThumbnailMode}
      style={{ right, left, ...options, ...verticalSpacing }}
    >
      {children}
    </Container>
  );
};

const Container = styled.div<{
  _width: string | number;
  _thumbnailMode?: boolean;
  _openWindow: boolean;
}>`
  position: absolute;

  width: ${({ _width }) => _width}px;

  transition: ${({ _thumbnailMode }) =>
    _thumbnailMode
      ? 'none'
      : '0.3s ease opacity, 0.3s ease margin, 0.3s ease visibility'};

  ${({ _openWindow }) =>
    _openWindow
      ? `
    pointer-events: all;
    touch-action: auto;
    visibility: visible;
    margin: 20px 0;
    opacity: 1;
  `
      : `
    pointer-events: none;
    touch-action: none;
    visibility: hidden;
    margin: 0;
    opacity: 0;
  `}
`;
